import React, { FC, useEffect, useState } from 'react'
import {
    OrderFragment,
    useCalculateOrderQuery,
    usePlaceOrderMutation
} from '../__generated__/order'
import { Point } from '../../../__generated__/types'
import { useReactiveVar } from '@apollo/client'
import {
    calculateOrderVar,
    placeOrderVar,
    activeOrderIdVar
} from 'store/cache'
import { ProfileContext } from 'context/ProfileContext'
import { ModalLayout } from 'components/layouts/ModalLayout/ModalLayout'
import { OrderButtonLink } from './OrderButtonLink/OrderButtonLink'

interface Props {
    distance: number
    points: Point[]
    hide: boolean
    hideChange: () => void
}

export const OrderButton: FC<Props> = ({
    distance,
    points,
    hide,
    hideChange
}) => {
    const placeOrder = useReactiveVar(placeOrderVar)
    const calculateOrder = useReactiveVar(calculateOrderVar)

    // Modals State
    const [price, updatePrice] = useState<number>(null!)
    const [openModal, setOpenModal] = useState(false)
    const [placed, setPlaced] = useState(false)
    const [isFetching, setIsFetching] = useState(false)
    const profile = React.useContext(ProfileContext)

    // Queries and Mutations
    const [usePlaceOrder, { data }] = usePlaceOrderMutation()
    const { refetch } = useCalculateOrderQuery({
        fetchPolicy: 'standby',
        variables: {
            input: calculateOrder
        }
    })

    useEffect(() => {
        let cancelled = false
        refetch({
            input: { ...calculateOrder, range: Math.round(distance) }
        })
            .then((res) => {
                if (!cancelled && res.data?.calculate) {
                    updateCalculatedFields(res.data.calculate)
                }
            })
            .catch(() => {})
        // изменяем поле range в placeOrder
        placeOrderVar({ ...placeOrder, range: Math.round(distance) })
        return () => {
            cancelled = true
        }
    }, [distance, calculateOrder])

    // Добаваляет в placeOrder координаты адресов и меняет pointsLength.
    // Отправляем только поля RequiredPointInput (без __typename и гео-деталей).
    useEffect(() => {
        const newPoints = points.map((item) => ({
            sort: item.sort,
            address: item.address,
            latitude: item.latitude,
            longitude: item.longitude
        }))
        placeOrderVar({
            ...placeOrder,
            points: newPoints
        })
    }, [points.length])

    // После оформления заказа показываем подтверждение и открываем статус
    useEffect(() => {
        if (data?.placeOrder.id) {
            setIsFetching(false)
            setPlaced(true)
            activeOrderIdVar(data.placeOrder.id)
        }
    }, [data])

    const handlePlaceOrder = async () => {
        if (profile) {
            setIsFetching(true)
            await usePlaceOrder({
                variables: { input: placeOrder }
            })
        } else handleOpenModal()
    }

    const updateCalculatedFields = (order: OrderFragment) => {
        updatePrice(order.finalPrice)
    }

    const handleClosePlaced = () => {
        setPlaced(false)
        hideChange()
    }
    const handleOpenModal = () => setOpenModal(true)
    const handleCloseModal = () => setOpenModal(false)

    return (
        <>
            <OrderButtonLink
                price={price}
                carType={placeOrder.carType}
                points={points}
                handlePlaceOrder={handlePlaceOrder}
                hide={hide}
                hideChange={hideChange}
                isFetching={isFetching}
            />

            <ModalLayout open={placed} onClose={handleClosePlaced}>
                <h3>Заказ оформлен</h3>
                <p>Стоимость списана с вашего баланса.</p>
            </ModalLayout>
            <ModalLayout open={openModal} onClose={handleCloseModal}>
                <h3>Заполните профиль</h3>
            </ModalLayout>
        </>
    )
}
